import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { calculateFees, nextTierBenefit } from "@shared/fees";
import { cn } from "@/lib/utils";

const TIERS: { label: string; value: string; key: ReturnType<typeof calculateFees>["tier"] }[] = [
  { label: "Under $8", value: "$1.20 flat", key: "small" },
  { label: "$8 – $49.99", value: "15%", key: "standard" },
  { label: "$50 – $99.99", value: "13%", key: "medium" },
  { label: "$100+", value: "11%", key: "large" },
];

export function ServiceFeeInfo({
  subtotal,
  showUpsell = false,
}: {
  subtotal: number;
  showUpsell?: boolean;
}) {
  const fees = calculateFees({ subtotal });

  let upsell: string | null = null;
  if (showUpsell) {
    if (fees.tier === "small") {
      const headroom = Math.round((8 - subtotal) * 100) / 100;
      if (headroom > 0) {
        upsell = `Add up to $${headroom.toFixed(2)} more and your fee stays the same — anything you add right now is effectively free.`;
      }
    } else {
      const next = nextTierBenefit(subtotal);
      if (next) {
        upsell = `Add $${next.amountToAdd.toFixed(2)} more to drop your fee from ${next.currentRatePercent}% to ${next.nextRatePercent}%.`;
      }
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="How the service fee works"
          className="inline-flex items-center text-muted-foreground hover:text-foreground"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3">
        <p className="text-sm font-semibold">How the service fee works</p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Card processing alone costs 30¢ plus 2.9% on every order, no matter how
          small — so tiny orders cost the most to run. The fee covers that and keeps
          Stoop going. It gets proportionally cheaper the more you buy:
        </p>
        <div className="divide-y rounded-md border">
          {TIERS.map((t) => (
            <div
              key={t.key}
              className={cn(
                "flex justify-between px-3 py-1.5 text-xs",
                t.key === fees.tier && "bg-primary/10 font-semibold text-foreground",
              )}
            >
              <span>{t.label}</span>
              <span>{t.value}</span>
            </div>
          ))}
        </div>
        {upsell && <p className="text-xs font-medium text-primary">{upsell}</p>}
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          The seller still receives the full item price.
        </p>
      </PopoverContent>
    </Popover>
  );
}
